import { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'

import { currencyString, ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { useGetServiceTax } from '@user/store/serviceTax'
import { IServiceFrequency } from '@user/store/serviceFrequency'
import {
  IServiceProduct,
  SERVICE_TYPE,
  useGetBranchServiceProducts,
} from '@user/store/serviceProducts'

import { ILeadOppProductFormModel } from '../typings'

type Props = {
  entityId: number
  localMode: boolean
  entityType: ENTITY_TYPES
  serviceLocations: LocationDTO[]
  serviceProducts: IServiceProduct[]
  serviceFrequencies: IServiceFrequency[]
}

export function useUpdateFormFields(props: Props) {
  const {
    entityId,
    localMode,
    entityType,
    serviceProducts,
    serviceLocations,
    serviceFrequencies,
  } = props

  const { values, setValues, setFieldValue } =
    useFormikContext<ILeadOppProductFormModel>()

  const { serviceLocationId } = values
  const serviceLocationState = useMemo(() => {
    const location = serviceLocations.find((x) =>
      localMode
        ? x.externalLinkId === serviceLocationId
        : x.id === Number(serviceLocationId),
    )
    return location?.stateAbbreviation
  }, [localMode, serviceLocationId, serviceLocations])

  // update values based on service change
  const onChangeExternalId = useCallback(
    (_e, svc: IServiceProduct | null) => {
      if (!svc?.id) {
        setValues((v) => ({
          ...v,
          name: '',
          externalId: '',
          description: '',
          serviceType: SERVICE_TYPE.SERVICE,
        }))
        return
      }

      if (svc) {
        const newValues: Partial<ILeadOppProductFormModel> = {
          externalId: svc.id,
          name: svc.name,
          description: svc.description,
          serviceType: svc.serviceType,
          chargeTax:
            svc.taxableInStates.length > 0 &&
            svc.taxableInStates.some((x) => x.trim().length > 0)
              ? Boolean(
                  serviceLocationState &&
                    svc.taxableInStates.includes(serviceLocationState),
                )
              : svc.taxable,
        }

        if (svc.serviceType === SERVICE_TYPE.SERVICE) {
          const freq = serviceFrequencies.find(
            (f) => f.annualOccurrences == svc.annualOccurrences,
          )

          const frequencyId = freq?.id ?? ''
          const annualOccurrences = freq?.annualOccurrences ?? 0

          newValues.frequencyId = frequencyId
          newValues.annualOccurrences = annualOccurrences
          newValues.initialPrice = currencyString(svc.initialPrice)
          newValues.initialDiscountAmount = ''
          newValues.recurringDiscountAmount = ''
          newValues.recurringPrice =
            annualOccurrences === 0 ? '' : currencyString(svc.price)
        } else {
          newValues.frequencyId = ''
          newValues.annualOccurrences = 0
          newValues.initialPrice = currencyString(svc.price)
          newValues.recurringPrice = ''
          newValues.recurringDiscountAmount = ''
        }

        setValues((v) => ({ ...v, ...newValues }))
      }
    },
    [serviceFrequencies, setValues, serviceLocationState],
  )

  // update annualOccurrences based on frequency change
  const onChangeFrequencyId = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      oldFrequencyId: string,
      serviceId: string,
    ) => {
      const frequencyId = event.target.value
      const freq = serviceFrequencies.find((f) => f.id === frequencyId)
      const annualOccurrences = freq?.annualOccurrences ?? 0

      const newValues: Partial<ILeadOppProductFormModel> = {
        frequencyId,
        annualOccurrences,
      }

      if (annualOccurrences === 0) {
        newValues.recurringPrice = ''
        newValues.recurringDiscountAmount = ''
      } else {
        const svc = serviceProducts.find((s) => s.id === serviceId)
        const oldFreq = serviceFrequencies.find((f) => f.id === oldFrequencyId)

        if (svc?.price && (oldFreq?.annualOccurrences ?? 0) === 0) {
          newValues.recurringPrice = currencyString(svc.price)
        }
      }

      setValues((v) => ({ ...v, ...newValues }))
    },
    [serviceFrequencies, setValues, serviceProducts],
  )

  const {
    serviceTax,
    getForEntity,
    getForLocation,
    getForUnMatchedLocation,
    loading: taxLoading,
    status: taxStatus,
  } = useGetServiceTax()

  const { loading: servicesLoading, getServices } =
    useGetBranchServiceProducts()

  useEffect(() => {
    if (serviceLocationId) {
      const location = serviceLocations.find((x) =>
        localMode
          ? x.externalLinkId === serviceLocationId
          : x.id === Number(serviceLocationId),
      )

      if (location && location.externalLinkId) {
        getForLocation(location.externalLinkId)
      } else {
        getForUnMatchedLocation({
          entityId,
          entityType,
          locationId: Number(serviceLocationId),
        })
      }
    }
  }, [
    entityId,
    entityType,
    getForEntity,
    getForLocation,
    getForUnMatchedLocation,
    localMode,
    serviceLocationId,
    serviceLocations,
  ])

  useEffect(() => {
    if (serviceTax) {
      setFieldValue('taxRate', serviceTax.rate || serviceTax.taxRate || 0)
      setFieldValue('canadianTaxRate', serviceTax.canadianTaxRate)
      setFieldValue('gstHstLabel', serviceTax.gstHstLabel)
      setFieldValue('gstHstRate', serviceTax.gstHstRate || 0)
      setFieldValue('pstQstLabel', serviceTax.pstQstLabel)
      setFieldValue('pstQstRate', serviceTax.pstQstRate || 0)
    }
  }, [serviceTax, setFieldValue])

  useEffect(() => {
    if (serviceLocationId) {
      const location = serviceLocations.find((x) =>
        localMode
          ? x.externalLinkId === serviceLocationId
          : x.id === Number(serviceLocationId),
      )
      if (location && location.branchId != null) {
        getServices(location.branchId.toString())
      }
    }
  }, [getServices, localMode, serviceLocationId, serviceLocations])

  return {
    taxLoading,
    taxStatus,
    onChangeExternalId,
    onChangeFrequencyId,
    servicesLoading,
  }
}
